import { Card } from '@heidi/design-system/card';
import { Skeleton } from '@heidi/design-system/skeleton';
import { TypographyCaption } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { FormattedMessage } from 'react-intl';

/**
 * EVI-1403 #1 — Initial load unresponsiveness skeleton.
 *
 * Replaces the previous "empty container with a tiny spinner" loading state
 * that the SBS overlay rendered while `/chat/sbs/setup` was in flight (and
 * that each candidate card rendered while waiting for its first stream
 * chunk). The Loom ticket calls these out specifically — "Initial load is
 * too long (blank screen, no loading state, screen unresponsive)" — and the
 * designer's note is "Make container white (default bg)".
 *
 * The skeleton mirrors the actual `EvidenceSBSCandidate` shape (header strip
 * + scrollable content + sticky footer button) so the layout doesn't jank
 * when real content arrives — the eye locks onto the same regions.
 *
 * Background is explicitly `bg-surface` (white) so we never accidentally
 * inherit a tinted page background from a parent.
 */

const ContentLineSkeleton = ({ widthClass }: { widthClass: string }) => (
  <Skeleton className={cn('h-4', widthClass)} />
);

const KeyPointsSkeletonInline = () => (
  <div className="rounded-xl border border-border bg-surface p-4">
    <div className="mb-3 flex items-center gap-2">
      <Skeleton className="size-4 rounded" />
      <Skeleton className="h-4 w-20" />
    </div>
    <div className="space-y-2.5">
      {[80, 70, 65, 75].map((w) => (
        <div key={w} className="flex items-start gap-2">
          <Skeleton className="mt-1.5 size-1.5 shrink-0 rounded-full" />
          <Skeleton className="h-3.5" style={{ width: `${w}%` }} />
        </div>
      ))}
    </div>
  </div>
);

const ContentBlockSkeleton = () => (
  <div className="space-y-3">
    <Skeleton className="h-5 w-3/5" />
    <ContentLineSkeleton widthClass="w-full" />
    <ContentLineSkeleton widthClass="w-11/12" />
    <ContentLineSkeleton widthClass="w-4/5" />
  </div>
);

/**
 * Inner-content-only skeleton, intended to slot into the body of an already-
 * mounted candidate card (which has its own header + footer). Used by
 * `EvidenceSBSCandidate` when the candidate is loaded but its stream has not
 * produced its first chunk yet.
 */
export const EvidenceSBSCandidateContentSkeleton = ({
  className,
}: {
  className?: string;
}) => (
  <div
    className={cn('space-y-5 p-4', className)}
    data-testid="evidence-sbs-card-content-skeleton"
  >
    <KeyPointsSkeletonInline />
    <ContentBlockSkeleton />
  </div>
);

interface EvidenceSBSCandidateCardSkeletonProps {
  /** 1-based option number shown in the card header */
  index: number;
  className?: string;
}

/**
 * Single-card skeleton matching the `EvidenceSBSCandidateCard` shape:
 * header strip (Option N) + content area (key points + content) + footer
 * (disabled-style button placeholder). Used both inside the comparison
 * skeleton (during `/chat/sbs/setup`) and inside an individual candidate
 * card while its stream hasn't produced its first chunk.
 */
export const EvidenceSBSCandidateCardSkeleton = ({
  index,
  className,
}: EvidenceSBSCandidateCardSkeletonProps) => {
  return (
    <Card
      className={cn(
        'relative flex h-full min-h-80 flex-col overflow-hidden bg-surface',
        className
      )}
      data-testid="evidence-sbs-candidate-skeleton"
    >
      {/* Header — same shape/border as the real card so layout doesn't shift */}
      <div className="shrink-0 border-b px-4 py-2">
        <TypographyCaption className="text-text-secondary">
          <FormattedMessage
            id="NxWYm3JcW9"
            defaultMessage="Option {index}"
            values={{ index }}
          />
        </TypographyCaption>
      </div>

      {/* Content area */}
      <div className="min-h-0 flex-1 space-y-5 overflow-hidden p-4">
        <KeyPointsSkeletonInline />
        <ContentBlockSkeleton />
      </div>

      {/* Footer — placeholder for the "Prefer this response" button */}
      <div className="shrink-0 border-t bg-surface px-4 py-3">
        <Skeleton className="h-9 w-full" />
      </div>
    </Card>
  );
};
